import React, { useEffect, useRef, useState } from 'react';

// Shared presentational motion helpers (count-up, sparkline). No data or business logic.
export const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Counts 0 → value once (≈900 ms, eased). Static when reduced motion or value unavailable. */
export const useCountUp = (value, ms = 900) => {
  const [n, setN] = useState(reducedMotion() ? value : 0);
  useEffect(() => {
    if (value == null) return;
    if (reducedMotion()) { setN(value); return; }
    let raf; const t0 = performance.now();
    const tick = (t) => { const p = Math.min(1, (t - t0) / ms); setN(Math.round(value * (1 - Math.pow(1 - p, 3)))); if (p < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, ms]);
  return value == null ? null : n;
};

/** Tiny SVG sparkline with area fill; draws in on mount (stroke-dash), static under reduced motion. */
export const Sparkline = ({ data = [], color = '#7c3aed', w = 120, h = 44, testid = 'kpi-sparkline' }) => {
  const ref = useRef(null);
  const max = Math.max(1, ...data); const min = Math.min(...data, 0);
  const pts = data.map((v, i) => [(i / Math.max(1, data.length - 1)) * (w - 4) + 2, h - 4 - ((v - min) / Math.max(1, max - min)) * (h - 10)]);
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const area = `${d} L${(w - 2).toFixed(1)},${h} L2,${h} Z`;
  useEffect(() => { const el = ref.current; if (!el || reducedMotion()) return; const len = el.getTotalLength(); el.style.strokeDasharray = len; el.style.strokeDashoffset = len; el.getBoundingClientRect(); el.style.transition = 'stroke-dashoffset 900ms ease-out'; el.style.strokeDashoffset = 0; }, [d]);
  const id = `sg-${color.replace('#', '')}`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="overflow-visible" aria-hidden="true" data-testid={testid}>
      <defs><linearGradient id={id} x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity="0.28" /><stop offset="100%" stopColor={color} stopOpacity="0" /></linearGradient></defs>
      {pts.length > 1 && <path d={area} fill={`url(#${id})`} />}
      {pts.length > 1 && <path ref={ref} d={d} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
    </svg>
  );
};
